import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {BenjiKIVStatics} from "../../BenjiKIVStatics";
import {BenjiKIVSpine} from "../../components/BenjiKIVSpine";
import {BenjiKIV} from "../../BenjiKIV";
import {SpineUtils} from "../../utils/SpineUtils";
import {SpineTween} from "@nolimitcity/slot-game/bin/animation/SpineTween";
import {BenjiKIVGameData, BetWayWins} from "../../server/BenjiKIVData";
import {gsap} from "gsap";
import {BenjiKIVGameMode} from "../../server/BenjiKIVGameMode";
import {BenjiKIVSymbol} from "../../reels/BenjiKIVSymbol";
import {BenjiKIVSymbolStates} from "../../../core/reel/ReelInterfaces";

export class BenjiKIVIndividualWinView extends PIXI.Container implements StageLayer {
    private _currentTimeline:gsap.core.Timeline | null;
    private _stopped:boolean;
    private _betWayWins:BetWayWins[] = [];
    private _allSubTls:gsap.core.Timeline[] = [];

    constructor() {
        super();
        this.name = "BenjiKIVIndividualWinView";
        this.position = BenjiKIVStatics.MAIN_REELS_VIEW_POSITION;
    }

    public start():void {
        this._stopped = false;
        const data:BenjiKIVGameData = BenjiKIV.model.gameData;

        this._allSubTls = [];

        if(data && data.nextMode === BenjiKIVGameMode.NORMAL && data.betWayWins.length > 0 && data.totalSpinWinnings > 0) {
            this._betWayWins = JSON.parse(JSON.stringify(data.betWayWins));
            if(this._betWayWins.length > 1) {
                const allGroups:BetWayWins = JSON.parse(JSON.stringify(this._betWayWins[0]));

                allGroups.winPositions = BenjiKIV.model.allWinSymPositions;
                allGroups.multipliedAmount = BenjiKIV.model.totalSpinWinnings;
                allGroups.numberOfCombinations = BenjiKIV.model.totalWaysWon;

                this._betWayWins.push(allGroups);
            }

            // Add a small delay for the first time, so we don't start to play before the autoPlay start next spin.
            this.playNextBetWin(0, 1);
            this.resetAllSymbolsToPreviousState();
        }
    }

    public stop():void {
        this._stopped = true;
        this._betWayWins = [];

        if(!BenjiKIV.model.gameData) {
            return;
        }

        if(this._currentTimeline && this._currentTimeline.isActive()) {
            this._currentTimeline.pause();
            this._currentTimeline.kill();
        }
        this._currentTimeline = null;

        BenjiKIV.multiplierCountUp.hideAllMultipliers();
        this.clearUsedSpines();

        // #225
        this._allSubTls.forEach((tl:gsap.core.Timeline | null) => {
            if(tl) {
                tl.pause();
                tl.kill();

                tl = null;
            }
        });
        this.removeChildren();
    }

    private playNextBetWin(index:number, delay:number):void {
        if(this._stopped) {
            return;
        }

        const data:BenjiKIVGameData = BenjiKIV.model.gameData;
        if(index === this._betWayWins.length) {
            index = 0;

            BenjiKIV.winWays.hideToggleWinWays();
            this.resetAllSymbolsToPreviousState();
            BenjiKIV.multiplierCountUp.hideAllMultipliers();

            if(!this._stopped) {
                this.playNextBetWin(index, 0.5);
            }

            return;
        }
        const tl:gsap.core.Timeline = gsap.timeline();

        if(index === 0) {
            BenjiKIV.multiplierCountUp.setYPos(true, false);
        }

        if(data.brokeTheBank) {
            const allGroups:BetWayWins = JSON.parse(JSON.stringify(this._betWayWins[0]));

            allGroups.winPositions = BenjiKIV.model.allWinSymPositions;
            allGroups.multipliedAmount = BenjiKIV.model.accumulatedRoundWin;
            allGroups.multiplier = 1; // don't show multiplier

            tl.add(this.getBetWayAnimation(allGroups, false), delay);
        } else {
            tl.add(this.getBetWayAnimation(this._betWayWins[index], (index === this._betWayWins.length - 1)), delay);
            tl.add(() => {
                if(!this._stopped) {
                    this.playNextBetWin(index + 1, 0);
                }
            });
        }

        this._currentTimeline = tl;
    }

    private getBetWayAnimation(winData:BetWayWins, allAtOnce:boolean):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline(),
            subTLs:gsap.core.Timeline[] = [],
            winRatio:number = BenjiKIV.model.winRatio,
            baseName:string = (winRatio > 5 ? "Toggle_Large_" : "Toggle_");
        let animationName:string,
            symbol:BenjiKIVSymbol;

        tl.add(() => {
            if(!this._stopped) {
                if(allAtOnce) {
                    BenjiKIV.multiplierCountUp.summariseMultiplierCountUp();
                } else if(winData.multiplier > 1) {
                    BenjiKIV.multiplierCountUp.show1Multiplier(0);
                    BenjiKIV.multiplierCountUp.setMultiplier(0, winData.multiplier);
                }

                BenjiKIV.winWays.showToggleWinWays(winData.multipliedAmount, winData.numberOfCombinations);
            }

            this.clearUsedSpines();
        }, 0);

        winData.winPositions.forEach((reelWin:boolean[], i:number) => {
            reelWin.forEach((symWin:boolean, j:number) => {
                if(symWin) {
                    animationName = baseName;
                    symbol = (BenjiKIV.allReelView.reelModels[i].reelView.symbols[j + 3] as BenjiKIVSymbol);

                    BenjiKIV.overlayScatterPool.wakeUpOverlayToIdle(i, (j + 3));

                    subTLs.push(symbol.getNewStateAnimation(symbol.state, 0.001));

                    if((!BenjiKIV.model.reelsStackedStatus[i] || (BenjiKIV.model.reelsStackedStatus[i] && j === 1))) {
                        if(symbol.splitLevel > 1) {
                            //Split symbol. Need two half size frames
                            animationName += "1";
                            subTLs.push(this.getWinTL(animationName, i, j, 0));
                            subTLs.push(this.getWinTL(animationName, i, j, 1));
                        } else {
                            //Part of or fully visible stacked at bottom of reel
                            if(symbol.stackedSymbolName.indexOf("_TOP") > -1) {
                                //If only top of stacked is visible, play normal frame
                                if((j + 3) === 5) {
                                    // #213
                                    subTLs.push((<BenjiKIVSymbol>BenjiKIV.allReelView.reelModels[i].reelView.symbols[j + 4]).getNewStateAnimation(BenjiKIVSymbolStates.NORMAL, 0.001));

                                    animationName += "2";
                                    subTLs.push(this.getWinTL(animationName, i, j, 2));
                                }
                            }
                            //Part of or fully visible stacked at top of reel
                            else if(symbol.stackedSymbolName.indexOf("_BOT") > -1) {
                                //If only bottom of stacked is visible, play normal frame
                                if((j + 3) === 3) {
                                    // #213
                                    subTLs.push((<BenjiKIVSymbol>BenjiKIV.allReelView.reelModels[i].reelView.symbols[j + 2]).getNewStateAnimation(BenjiKIVSymbolStates.NORMAL, 0.001));

                                    animationName += "2";
                                    subTLs.push(this.getWinTL(animationName, i, j, 2));
                                }
                            } else if(symbol.stackedSymbolName.indexOf("_MID") > -1) {
                                if((j + 3) === 4) {
                                    animationName += "4";
                                    subTLs.push(this.getWinTL(animationName, i, j, 5));
                                }
                                if((j + 3) === 3) {
                                    animationName += "3";
                                    subTLs.push(this.getWinTL(animationName, i, j, 3));
                                }
                                if((j + 3) === 5) {
                                    animationName += "3";
                                    subTLs.push(this.getWinTL(animationName, i, j, 4));
                                }
                            } else {
                                // #213
                                subTLs.push(symbol.getNewStateAnimation(BenjiKIVSymbolStates.NORMAL, 0.001));

                                //Normal sym
                                animationName += "2";
                                subTLs.push(this.getWinTL(animationName, i, j, 2));
                            }
                        }
                    }
                } else {
                    let symbolPosition:number = (j + 3);
                    if(symbolPosition === 3 && BenjiKIV.allReelView.reelModels[i].reelView.symbols[symbolPosition].stackedSymbolName.indexOf("_BOT") > -1) {
                        //Only bottom part of stacked is visible
                        tl.add(() => {
                            (<BenjiKIVSymbol>BenjiKIV.allReelView.reelModels[i].reelView.symbols[symbolPosition - 1]).getNewStateAnimation(BenjiKIVSymbolStates.NO_WIN, 0.001);
                        });
                    } else if(symbolPosition === 5 && BenjiKIV.allReelView.reelModels[i].reelView.symbols[symbolPosition].stackedSymbolName.indexOf("_TOP") > -1) {
                        //Only top part of stacked is visible
                        tl.add(() => {
                            (<BenjiKIVSymbol>BenjiKIV.allReelView.reelModels[i].reelView.symbols[symbolPosition + 1]).getNewStateAnimation(BenjiKIVSymbolStates.NO_WIN, 0.001);
                        });
                    } else {
                        tl.add(() => {
                            (<BenjiKIVSymbol>BenjiKIV.allReelView.reelModels[i].reelView.symbols[symbolPosition]).getNewStateAnimation(BenjiKIVSymbolStates.NO_WIN, 0.001);
                        });
                    }
                    tl.add(() => BenjiKIV.overlayScatterPool.playNoWin(i, symbolPosition));
                }
            });
        });

        tl.add(subTLs, 0);
        tl.to(this, {
            duration : 0.5
        });

        this._allSubTls = subTLs;

        return tl;
    }

    // Sizes: 0 = split top, 1 = split bottom, 2 = normal, 3 = stacked 2 sym top, 4 = stacked 2 sym bottom, 5 = full stacked
    private getWinTL(animationName:string, reel:number, row:number, size:number):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline(),
            animation:BenjiKIVSpine = BenjiKIV.individualWinSpinesPool.getAnimation();
        tl.add(() => {
            if(!this._stopped) {
                this.addChild(animation);
                this.resetSpineAnimation(animation);
            }
            animation.position.x = BenjiKIVStatics.symbolWidth * reel;
            animation.position.y = BenjiKIVStatics.symbolHeight * (row + 2);
            switch(size) {
                case 0:
                    animation.position.y -= BenjiKIVStatics.symbolHeight / 4;
                    break;
                case 1:
                    animation.position.y += BenjiKIVStatics.symbolHeight / 4;
                    break;
                case 3:
                    animation.position.y += BenjiKIVStatics.symbolHeight / 2;
                    break;
                case 4:
                    animation.position.y -= BenjiKIVStatics.symbolHeight / 2;
                    break;
                default:
            }
        });
        tl.add(new SpineTween(animation, 0, animationName));
        tl.add(() => {
            if(animation.parent) {
                this.removeChild(animation);
            }
        });

        return tl;
    }

    private resetSpineAnimation(spineAnimation:BenjiKIVSpine):void {
        spineAnimation.visible = true;
        SpineUtils.resetSpineAnimation(spineAnimation);
    }

    private resetAllSymbolsToPreviousState():void {
        BenjiKIV.overlayScatterPool.wakeUpAllOverlaysToIdle();
        for(let i:number = 0; i < 5; i++) {
            const symbols:BenjiKIVSymbol[] = BenjiKIV.allReelView.reelModels[i].reelView.symbols as BenjiKIVSymbol[];
            symbols.forEach((sym:BenjiKIVSymbol) => {
                sym.getNewStateAnimation(sym.state, 0.1);
            });
        }
    }

    private clearUsedSpines():void {
        const usedSpines:BenjiKIVSpine[] = BenjiKIV.individualWinSpinesPool.getAllUsed();
        for(let i:number = 0; i < usedSpines.length; i++) {
            usedSpines[i].visible = false;
            usedSpines[i].isUsed = false;

            if(usedSpines[i].parent) {
                usedSpines[i].parent.removeChild(usedSpines[i]);
            }
        }
    }
    get individualWinPlaying():boolean{
        return this._stopped;
    }
}